// Firm Settings — SuperAdmin edits the current firm's brand,
// letterhead, contact info, and colors. Reads from GET /api/current-firm,
// writes back via PATCH /api/current-firm. On save, invalidates the
// firm cache server-side and asks the parent to refetch so the sidebar
// (and any other component consuming currentFirm) updates immediately.

import React, { useEffect, useState } from "react";
import { C, roleAtLeast } from "../constants.js";
import { PracticePolicySection } from "./_practice_policy_section.jsx";
import { WorkflowTimingSection } from "./_workflow_timing_section.jsx";
import { SolRulesSection } from "./_sol_rules_section.jsx";
import { apiFetch } from "../api.js";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC",
];

export function FirmSettingsPage({ authStatus, currentFirm, onFirmChange, showNotification }) {
  const role = authStatus?.firm_user?.role || "pending";
  // canEdit gate: firm_admin+ can edit firm settings AND upload/delete
  // templates. platform_operator (Charpen ops) inherits by rank. The old
  // strict-equality check on "superadmin" hid controls from anyone at
  // firm_admin OR platform_operator, which was wrong post-4-λ role
  // expansion — that renamed superadmin → firm_admin and added
  // platform_operator above it.
  const canEdit = roleAtLeast(role, "firm_admin");

  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Hydrate the form when currentFirm arrives (async load in app.jsx).
  useEffect(() => {
    if (!currentFirm) return;
    setForm({
      display_name: currentFirm.display_name || "",
      short_name: currentFirm.short_name || "",
      letterhead_name: currentFirm.letterhead_name || "",
      letterhead_address: currentFirm.letterhead_address || "",
      primary_phone: currentFirm.primary_phone || "",
      primary_fax: currentFirm.primary_fax || "",
      primary_email: currentFirm.primary_email || "",
      states_of_practice: Array.isArray(currentFirm.states_of_practice)
        ? currentFirm.states_of_practice
        : [],
      brand_primary_color: currentFirm.brand_primary_color || "#1B2A4A",
      brand_secondary_color: currentFirm.brand_secondary_color || "#C6993E",
      logo_url: currentFirm.logo_url || "",
      portal_base_url: currentFirm.portal_base_url || "",
    });
    setDirty(false);
  }, [currentFirm]);

  const update = (field) => (e) => {
    const value = e && e.target ? e.target.value : e;
    setForm(f => ({ ...f, [field]: value }));
    setDirty(true);
  };

  const toggleState = (code) => {
    setForm(f => {
      const cur = f.states_of_practice || [];
      const next = cur.includes(code) ? cur.filter(s => s !== code) : [...cur, code];
      return { ...f, states_of_practice: next };
    });
    setDirty(true);
  };

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      await apiFetch("/api/current-firm", {
        method: "PATCH",
        body: JSON.stringify(form),
      });
      setDirty(false);
      showNotification?.("Firm settings saved.", "success");
      onFirmChange?.();  // ask App to refetch /api/current-firm
    } catch (e) {
      if (e.status === 403) {
        showNotification?.("Only SuperAdmins can edit firm settings.", "warning");
      } else {
        showNotification?.(`Save failed: ${e.message}`, "error");
      }
    }
    setSaving(false);
  };

  if (!form) {
    return React.createElement("div", { style: { padding: "40px", color: C.textMuted } },
      "Loading firm settings…"
    );
  }

  return React.createElement("div", { className: "fade-in", style: { padding: "32px 40px", maxWidth: "900px" } },
    // Header
    React.createElement("div", { style: { marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "flex-end" } },
      React.createElement("div", null,
        React.createElement("h1", {
          style: { fontFamily: "'Playfair Display',serif", fontSize: "28px", fontWeight: 600, color: C.navy, marginBottom: "6px" }
        }, "Firm Settings"),
        React.createElement("p", { style: { fontSize: "14px", color: C.textMuted } },
          canEdit
            ? "Update your firm's brand, letterhead, and contact info. Changes are live for every user immediately."
            : "Read-only. SuperAdmins can edit these values.",
        ),
      ),
      React.createElement("button", {
        onClick: save,
        disabled: !dirty || saving || !canEdit,
        style: {
          padding: "10px 20px", borderRadius: "8px", border: "none",
          background: (!dirty || saving || !canEdit) ? C.warmGray : C.gold,
          color: "#fff", fontSize: "14px", fontWeight: 600,
          cursor: (!dirty || saving || !canEdit) ? "default" : "pointer",
        }
      }, saving ? "Saving…" : dirty ? "Save changes" : "Saved"),
    ),

    // ─── Identity block ────────────────────────────────────────
    _section("Identity",
      _field("Display name", "Shown in the sidebar and login page.",
        _textInput(form.display_name, update("display_name"), canEdit),
      ),
      _field("Short name", "Used for the collapsed-sidebar monogram and email display names.",
        _textInput(form.short_name, update("short_name"), canEdit),
      ),
      _field("Slug (read-only)", "Firm URL slug. Immutable — changing this would break existing subdomain URLs and client bookmarks.",
        _textInput(currentFirm.slug || "", () => {}, false),
      ),
    ),

    // ─── Letterhead + contact block ────────────────────────────
    _section("Letterhead & contact",
      _field("Letterhead name", "How the firm name appears on generated letters (may differ from display name, e.g., include LLP/LLC suffix).",
        _textInput(form.letterhead_name, update("letterhead_name"), canEdit),
      ),
      _field("Letterhead address", "Full mailing address as it should appear on letterhead. Multi-line.",
        _textArea(form.letterhead_address, update("letterhead_address"), canEdit, 3),
      ),
      _field("Primary phone", null,
        _textInput(form.primary_phone, update("primary_phone"), canEdit),
      ),
      _field("Primary fax", null,
        _textInput(form.primary_fax, update("primary_fax"), canEdit),
      ),
      _field("Primary email", "Public contact address. Not the same as your Clio login.",
        _textInput(form.primary_email, update("primary_email"), canEdit),
      ),
    ),

    // ─── States of practice ────────────────────────────────────
    _section("States of practice",
      React.createElement("p", { style: { fontSize: "13px", color: C.textMuted, marginBottom: "8px" } },
        "Where the firm practices. Drives state-specific dropdowns like crash-report vendors.",
      ),
      React.createElement("div", {
        style: { display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "8px" }
      },
        ...US_STATES.map(code =>
          React.createElement("button", {
            key: code,
            onClick: canEdit ? () => toggleState(code) : undefined,
            disabled: !canEdit,
            style: {
              padding: "6px 10px",
              border: `1px solid ${form.states_of_practice.includes(code) ? C.gold : C.warmGray}`,
              background: form.states_of_practice.includes(code) ? C.gold : "#fff",
              color: form.states_of_practice.includes(code) ? "#fff" : C.textDark,
              borderRadius: "6px", fontSize: "12px", fontWeight: 600,
              cursor: canEdit ? "pointer" : "default", minWidth: "36px",
            }
          }, code)
        ),
      ),
    ),

    // FIRST on the page, deliberately.
    //
    // It is the most consequential unset value in the app: fees, the
    // settlement statement's net figures and every revenue number
    // depend on it, and it had been sitting below the letterhead
    // fields. Three answers, and three screens light up.
    React.createElement(FeeAgreementSection, { canEdit, showNotification }),

    // ─── Branding block ────────────────────────────────────────
    _section("Branding",
      _field("Primary color", "Main brand color. Used for headings and accents.",
        _colorInput(form.brand_primary_color, update("brand_primary_color"), canEdit),
      ),
      _field("Secondary / accent color", "Highlights, buttons, callouts.",
        _colorInput(form.brand_secondary_color, update("brand_secondary_color"), canEdit),
      ),
      // Substep 5-ε: real logo upload widget. Stored in Postgres and
      // auto-inserted at any {{firm_logo}} placeholder in generated
      // templates — templates no longer need to bake in the logo.
      React.createElement(LogoUploadField, {
        canEdit, showNotification, key: "logo-upload",
      }),
    ),


    // ─── Client portal block ───────────────────────────────────
    _section("Client portal",
      _field(
        "Portal base URL",
        "Where clients open magic-link emails. e.g. https://mcb.shinglelaw.com — leave blank to fall back to the app's default base URL. Include https:// and no trailing slash.",
        _textInput(form.portal_base_url, update("portal_base_url"), canEdit),
      ),
    ),

    // ─── Practice policy ───────────────────────────────────────
    // The numbers that decide how a case is judged. Sits above the
    // template and credential blocks because it is the part a partner
    // would come here to change.
    React.createElement(PracticePolicySection, {
      canEdit, showNotification, key: "practice-policy",
    }),

    React.createElement(WorkflowTimingSection, {
      showNotification, key: "workflow-timing",
    }),

    React.createElement(SolRulesSection, {
      showNotification, key: "sol-rules",
    }),

    // ─── Firm templates block (Phase 5-α.2) ────────────────────
    // Self-contained sub-component with its own async state — dirty/
    // saving here doesn't intersect with the firm-brand form above.
    React.createElement(TemplatesSection, {
      canEdit, showNotification, key: "templates-section",
    }),

    // ─── Firm Vault block (Substep 5-θ.2) ──────────────────────
    // Encrypted per-firm credentials for external services referenced
    // from SOPs. See app/services/firm_credentials.py.
    React.createElement(VaultSection, {
      canEdit, showNotification, key: "vault-section",
    }),

    // ─── Firm Locations block (Substep 5-κ.2) ──────────────────
    // Multi-office CRUD. Attorneys pick their location in My Profile
    // (5-κ.3); doc generation resolves letterhead from responsible
    // attorney's location (5-κ.4).
    React.createElement(LocationsSection, {
      canEdit, showNotification, key: "locations-section",
    }),
  );
}


// ─── Templates section — DB-backed .docx uploads (Phase 5-α.2) ──

function TemplatesSection({ canEdit, showNotification }) {
  const [templates, setTemplates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  // Upload metadata form — collapsed until a file is picked.
  const [pendingFile, setPendingFile] = useState(null);
  const [meta, setMeta] = useState({ display_name: "", description: "", category: "", matter_stage: "", requires_provider: false });
  // AI field-detection modal state (Substep 5-α.4)
  const [aiModal, setAiModal] = useState(null);          // {template, suggestions, checked: Set<idx>}
  const [aiRunning, setAiRunning] = useState(false);
  const [aiApplying, setAiApplying] = useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiFetch("/api/firm-templates");
      setTemplates(d.data || []);
    } catch (e) {
      if (e.status === 403) {
        showNotification?.("Only paralegals and above can view firm templates.", "warning");
      } else {
        showNotification?.(`Failed to load templates: ${e.message}`, "error");
      }
      setTemplates([]);
    }
    setLoading(false);
  }, [showNotification]);

  useEffect(() => { load(); }, [load]);

  const pickFile = (file) => {
    if (!file) return;
    // Sanity checks matching the backend so users get instant feedback
    // rather than a 422 round-trip.
    if (!file.name.toLowerCase().endsWith(".docx")) {
      showNotification?.("Only .docx files are supported.", "warning");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      showNotification?.("File too large (max 20 MB).", "warning");
      return;
    }
    setPendingFile(file);
    // Prefill display name from the filename minus extension + snake→title.
    const base = file.name.replace(/\.docx$/i, "").replace(/[_-]+/g, " ").trim();
    const title = base.replace(/\b\w/g, c => c.toUpperCase());
    setMeta(m => ({ ...m, display_name: title }));
  };

  const submitUpload = async () => {
    if (!pendingFile || uploading) return;
    if (!meta.display_name.trim()) {
      showNotification?.("Give the template a display name.", "warning");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", pendingFile);
      fd.append("display_name", meta.display_name.trim());
      if (meta.description) fd.append("description", meta.description);
      if (meta.category) fd.append("category", meta.category);
      if (meta.matter_stage) fd.append("matter_stage", meta.matter_stage);
      fd.append("requires_provider", meta.requires_provider ? "true" : "false");

      // apiFetch always sets Content-Type: application/json — bad for
      // multipart. Use bare fetch here + credentials so the session
      // cookie still comes through.
      const res = await fetch("/api/firm-templates", {
        method: "POST", body: fd, credentials: "same-origin",
      });
      if (!res.ok) {
        let msg = res.statusText;
        try { const j = await res.json(); msg = j.detail || msg; } catch {}
        throw new Error(typeof msg === "string" ? msg : JSON.stringify(msg));
      }
      showNotification?.(`Uploaded "${meta.display_name}".`, "success");
      setPendingFile(null);
      setMeta({ display_name: "", description: "", category: "", matter_stage: "", requires_provider: false });
      await load();
    } catch (e) {
      showNotification?.(`Upload failed: ${e.message}`, "error");
    }
    setUploading(false);
  };

  const downloadTemplate = async (t) => {
    try {
      const res = await fetch(`/api/firm-templates/${t.id}/download`, {
        credentials: "same-origin",
      });
      if (!res.ok) throw new Error(res.statusText);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = t.filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      showNotification?.(`Download failed: ${e.message}`, "error");
    }
  };

  // ─── Merge-fields editor (Substep 5-α.4 + follow-up) ───────────
  // Modal opens EMPTY — user can either request AI suggestions or
  // add manual substitutions (or both). Firm Admin gate on both
  // endpoints.
  const openMergeFieldsModal = async (t) => {
    setAiModal({
      template: t, suggestions: [], checked: new Set(),
      textPreview: null, textLoading: true, unmatched: 0,
    });
    // Fetch just the text (cheap, no AI cost) so the user has the
    // document contents visible while they add manual substitutions.
    try {
      const res = await fetch(`/api/firm-templates/${t.id}/text`, {
        credentials: "same-origin",
      });
      if (res.ok) {
        const data = await res.json();
        setAiModal(m => m && m.template.id === t.id
          ? { ...m, textPreview: data.text || "", textLoading: false }
          : m);
      } else {
        setAiModal(m => m && m.template.id === t.id
          ? { ...m, textLoading: false }
          : m);
      }
    } catch (e) {
      setAiModal(m => m && m.template.id === t.id
        ? { ...m, textLoading: false }
        : m);
    }
  };

  const runAiSuggestions = async () => {
    if (!aiModal || aiRunning) return;
    setAiRunning(true);
    try {
      const res = await fetch(`/api/firm-templates/${aiModal.template.id}/analyze-fields`, {
        method: "POST", credentials: "same-origin",
      });
      if (!res.ok) {
        let msg = res.statusText;
        try { const j = await res.json(); msg = j.detail || msg; } catch {}
        throw new Error(msg);
      }
      const data = await res.json();
      // Merge with any existing (manual) suggestions rather than
      // clobber them. Skip duplicates (same original_text) — the
      // user's manual entry wins.
      setAiModal(m => {
        if (!m) return m;
        const existingKeys = new Set(m.suggestions.map(s => s.original_text));
        const fresh = (data.suggestions || []).filter(s => !existingKeys.has(s.original_text));
        const combined = [...m.suggestions, ...fresh];
        // Auto-check the freshly added AI suggestions with conf >= 0.5
        const nextChecked = new Set(m.checked);
        fresh.forEach((s, i) => {
          const globalIdx = m.suggestions.length + i;
          if ((s.confidence ?? 0) >= 0.5) nextChecked.add(globalIdx);
        });
        return {
          ...m,
          suggestions: combined,
          checked: nextChecked,
          textPreview: data.text_preview || m.textPreview,
          unmatched: (m.unmatched || 0) + (data.unmatched_count || 0),
        };
      });
      showNotification?.(
        `AI found ${data.suggestions?.length || 0} potential merge field${(data.suggestions?.length || 0) === 1 ? "" : "s"}.`,
        "success",
      );
    } catch (e) {
      showNotification?.(`AI analysis failed: ${e.message}`, "error");
    }
    setAiRunning(false);
  };

  const addManualSubstitution = () => {
    setAiModal(m => {
      if (!m) return m;
      const newIdx = m.suggestions.length;
      const next = {
        original_text: "",
        suggested_field: "",
        placeholder: "{{}}",
        field_type: "text",
        confidence: 1.0,     // manual entries are user-authored, not guessed
        context: "",
        is_new_field: true,
        notes: "manual",
      };
      const nextChecked = new Set(m.checked);
      nextChecked.add(newIdx);
      return { ...m, suggestions: [...m.suggestions, next], checked: nextChecked };
    });
  };

  const updateSuggestionOriginal = (idx, newOriginal) => {
    setAiModal(m => {
      if (!m) return m;
      const next = m.suggestions.map((s, i) =>
        i === idx ? { ...s, original_text: newOriginal } : s);
      return { ...m, suggestions: next };
    });
  };

  const removeSuggestion = (idx) => {
    setAiModal(m => {
      if (!m) return m;
      const next = m.suggestions.filter((_, i) => i !== idx);
      // Rebuild checked set with re-indexed positions
      const nextChecked = new Set();
      [...m.checked].forEach(oldIdx => {
        if (oldIdx < idx) nextChecked.add(oldIdx);
        else if (oldIdx > idx) nextChecked.add(oldIdx - 1);
      });
      return { ...m, suggestions: next, checked: nextChecked };
    });
  };

  const toggleSuggestion = (idx) => {
    setAiModal(m => {
      if (!m) return m;
      const next = new Set(m.checked);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return { ...m, checked: next };
    });
  };

  const updateSuggestionField = (idx, newFieldName) => {
    // Let the user rename a suggested field before applying — e.g.,
    // AI proposes "date_of_loss" but the firm uses "loss_date".
    setAiModal(m => {
      if (!m) return m;
      const next = m.suggestions.map((s, i) => {
        if (i !== idx) return s;
        const clean = (newFieldName || "").toLowerCase().replace(/[^a-z0-9_]+/g, "_").replace(/^_+|_+$/g, "");
        return {
          ...s,
          suggested_field: clean || s.suggested_field,
          placeholder: "{{" + (clean || s.suggested_field) + "}}",
        };
      });
      return { ...m, suggestions: next };
    });
  };

  const applyAi = async () => {
    if (!aiModal) return;
    const picks = [...aiModal.checked].map(i => aiModal.suggestions[i]).filter(Boolean);
    if (picks.length === 0) {
      showNotification?.("Nothing selected to apply.", "warning");
      return;
    }
    if (!window.confirm(
      `Rewrite "${aiModal.template.name || aiModal.template.filename}" with ${picks.length} merge field${picks.length === 1 ? "" : "s"}?\n\nThis replaces the file content in place. Re-upload the original if the result isn't what you wanted.`
    )) return;
    setAiApplying(true);
    try {
      const res = await fetch(`/api/firm-templates/${aiModal.template.id}/apply-fields`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          substitutions: picks.map(s => ({
            original_text: s.original_text,
            placeholder: s.placeholder,
          })),
        }),
      });
      if (!res.ok) {
        let msg = res.statusText;
        try { const j = await res.json(); msg = j.detail || msg; } catch {}
        throw new Error(msg);
      }
      const data = await res.json();
      showNotification?.(
        `Applied ${data.substitutions_applied} merge field${data.substitutions_applied === 1 ? "" : "s"}. Download the template to review.`,
        "success",
      );
      setAiModal(null);
      await load();
    } catch (e) {
      showNotification?.(`Apply failed: ${e.message}`, "error");
    }
    setAiApplying(false);
  };

  const deleteTemplate = async (t) => {
    if (!window.confirm(`Delete template "${t.name}" (${t.filename})?\n\nThis cannot be undone.`)) return;
    try {
      const res = await fetch(`/api/firm-templates/${t.id}`, {
        method: "DELETE", credentials: "same-origin",
      });
      if (!res.ok) {
        let msg = res.statusText;
        try { const j = await res.json(); msg = j.detail || msg; } catch {}
        if (res.status === 403) {
          showNotification?.("Only Firm Admins can delete templates.", "warning");
        } else {
          throw new Error(typeof msg === "string" ? msg : JSON.stringify(msg));
        }
        return;
      }
      showNotification?.("Template deleted.", "success");
      await load();
    } catch (e) {
      showNotification?.(`Delete failed: ${e.message}`, "error");
    }
  };

  return _section("Templates",
    React.createElement("p", {
      style: { fontSize: "12px", color: C.textMuted, marginBottom: "12px" }
    }, `Upload your firm's .docx templates (letterhead, demand letter, LORs). Bytes persist in the database — safe across redeploys. Max 20 MB per file.${!canEdit ? " Firm Admin required to upload or delete." : ""}`),

    // Drop zone + file picker
    canEdit && !pendingFile && React.createElement("label", {
      onDragOver: (e) => { e.preventDefault(); setDragOver(true); },
      onDragLeave: () => setDragOver(false),
      onDrop: (e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer?.files?.[0];
        if (file) pickFile(file);
      },
      style: {
        display: "block", padding: "24px", border: `2px dashed ${dragOver ? C.gold : C.warmGray}`,
        borderRadius: "8px", textAlign: "center", cursor: "pointer",
        background: dragOver ? "#FFF6E5" : C.warmWhite,
        color: C.textMuted, fontSize: "13px", marginBottom: "16px",
        transition: "background 0.15s, border-color 0.15s",
      },
    },
      React.createElement("div", { style: { fontWeight: 600, color: C.navy, marginBottom: "4px" } },
        "Drop a .docx here or click to pick"),
      React.createElement("div", null, "Up to 20 MB. Word documents only."),
      React.createElement("input", {
        type: "file", accept: ".docx", style: { display: "none" },
        onChange: (e) => pickFile(e.target.files?.[0]),
      }),
    ),

    // Metadata form appears once a file is picked
    canEdit && pendingFile && React.createElement("div", {
      style: {
        padding: "16px", border: `1px solid ${C.warmGray}`, borderRadius: "8px",
        background: C.warmWhite, marginBottom: "16px",
      },
    },
      React.createElement("div", { style: { fontSize: "13px", fontWeight: 600, color: C.navy, marginBottom: "10px" } },
        `Uploading: ${pendingFile.name} (${_fmtBytes(pendingFile.size)})`,
      ),
      _field("Display name", "How this template appears in dropdowns.",
        _textInput(meta.display_name, (e) => setMeta({ ...meta, display_name: e.target.value }), true),
      ),
      _field("Description (optional)", null,
        _textInput(meta.description, (e) => setMeta({ ...meta, description: e.target.value }), true),
      ),
      _field("Category (optional)", "e.g. LOR, Medical, Insurance, Demand, Settlement.",
        _textInput(meta.category, (e) => setMeta({ ...meta, category: e.target.value }), true),
      ),
      _field("Matter stage (optional)", "e.g. New Case, Treatment, Demand, Settlement.",
        _textInput(meta.matter_stage, (e) => setMeta({ ...meta, matter_stage: e.target.value }), true),
      ),
      React.createElement("label", {
        style: { display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: C.navy, marginTop: "8px" }
      },
        React.createElement("input", {
          type: "checkbox", checked: meta.requires_provider,
          onChange: (e) => setMeta({ ...meta, requires_provider: e.target.checked }),
        }),
        "Requires a medical provider to fill",
      ),
      React.createElement("div", { style: { display: "flex", gap: "8px", justifyContent: "flex-end", marginTop: "12px" } },
        React.createElement("button", {
          onClick: () => { setPendingFile(null); setMeta({ display_name: "", description: "", category: "", matter_stage: "", requires_provider: false }); },
          style: {
            padding: "8px 16px", borderRadius: "6px", border: `1px solid ${C.warmGray}`,
            background: "#fff", color: C.textMuted, fontSize: "13px", cursor: "pointer",
          },
        }, "Cancel"),
        React.createElement("button", {
          onClick: submitUpload,
          disabled: uploading || !meta.display_name.trim(),
          style: {
            padding: "8px 20px", borderRadius: "6px", border: "none",
            background: (uploading || !meta.display_name.trim()) ? C.warmGray : C.gold,
            color: "#fff", fontSize: "13px", fontWeight: 600,
            cursor: (uploading || !meta.display_name.trim()) ? "default" : "pointer",
          },
        }, uploading ? "Uploading…" : "Upload"),
      ),
    ),

    // Existing templates list
    loading
      ? React.createElement("div", { style: { color: C.textMuted, fontSize: "13px" } }, "Loading…")
      : (!templates || templates.length === 0)
        ? React.createElement("div", { style: { color: C.textMuted, fontSize: "13px", padding: "12px 0" } },
            "No templates uploaded yet.")
        : React.createElement("div", null,
            React.createElement("div", {
              style: {
                fontSize: "11px", color: C.textMuted, textTransform: "uppercase",
                letterSpacing: "0.06em", fontWeight: 600, marginBottom: "8px",
              }
            }, `${templates.length} template${templates.length === 1 ? "" : "s"}`),
            ...templates.map(t => React.createElement("div", {
              key: t.id,
              style: {
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "10px 0", borderBottom: `1px solid ${C.warmGray}`,
              },
            },
              React.createElement("div", null,
                React.createElement("div", { style: { fontSize: "13px", fontWeight: 600, color: C.navy } },
                  t.name || t.filename),
                React.createElement("div", { style: { fontSize: "11px", color: C.textMuted, marginTop: "2px" } },
                  t.filename +
                  (t.category ? " · " + t.category : "") +
                  " · " + _fmtBytes(t.size_bytes || 0) +
                  (t.uploaded_at ? " · uploaded " + t.uploaded_at.slice(0, 10) : "")
                ),
              ),
              React.createElement("div", { style: { display: "flex", gap: "6px" } },
                canEdit && React.createElement("button", {
                  onClick: () => openMergeFieldsModal(t),
                  title: "Edit merge fields — add manually or ask AI to detect them",
                  style: {
                    padding: "4px 10px", borderRadius: "6px", border: `1px solid ${C.gold}`,
                    background: "#fff", color: C.gold, fontSize: "11px",
                    fontWeight: 600, cursor: "pointer",
                  },
                }, "Edit merge fields"),
                React.createElement("button", {
                  onClick: () => downloadTemplate(t),
                  style: {
                    padding: "4px 10px", borderRadius: "6px", border: `1px solid ${C.warmGray}`,
                    background: "#fff", color: C.textDark, fontSize: "11px", cursor: "pointer",
                  },
                }, "Download"),
                canEdit && React.createElement("button", {
                  onClick: () => deleteTemplate(t),
                  style: {
                    padding: "4px 10px", borderRadius: "6px", border: `1px solid ${C.warmGray}`,
                    background: "#fff", color: C.error, fontSize: "11px", cursor: "pointer",
                  },
                }, "Delete"),
              ),
            )),
          ),

    // Merge-fields editor modal
    // Rendered as an element, not called as a function. Called, its
    // useState ran only when aiModal was truthy — a hook that appears
    // and disappears between renders, which React counts and throws on.
    aiModal && React.createElement(AiMergeFieldsModal, {
      aiModal,
      onClose: () => !aiApplying && setAiModal(null),
      onToggle: toggleSuggestion,
      onRename: updateSuggestionField,
      onEditOriginal: updateSuggestionOriginal,
      onRemove: removeSuggestion,
      onAddManual: addManualSubstitution,
      onRunAi: runAiSuggestions,
      onApply: applyAi,
      applying: aiApplying,
      aiRunning,
    }),
  );
}

function AiMergeFieldsModal({ aiModal, ...handlers }) {
  const { template, suggestions, checked, unmatched, textPreview, textLoading } = aiModal;
  const selectedCount = checked ? checked.size : 0;
  const [previewOpen, setPreviewOpen] = React.useState(false);

  return React.createElement("div", {
    onClick: (e) => { if (e.target === e.currentTarget) handlers.onClose(); },
    style: {
      position: "fixed", inset: 0, background: "rgba(27, 42, 74, 0.6)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000, padding: "40px 20px",
    }
  },
    React.createElement("div", {
      style: {
        background: "#fff", borderRadius: "12px", padding: "24px",
        maxWidth: "900px", width: "100%", maxHeight: "90vh",
        display: "flex", flexDirection: "column",
      }
    },
      // Header
      React.createElement("div", {
        style: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" }
      },
        React.createElement("div", null,
          React.createElement("h3", {
            style: { fontFamily: "'Playfair Display',serif", fontSize: "20px", color: C.navy, margin: 0 }
          }, "Edit merge fields"),
          React.createElement("div", {
            style: { fontSize: "12px", color: C.textMuted, marginTop: "4px" }
          }, `${template.name || template.filename} · ${suggestions.length} substitution${suggestions.length === 1 ? "" : "s"}${unmatched ? " · " + unmatched + " new field" + (unmatched === 1 ? "" : "s") : ""}`),
        ),
        React.createElement("button", {
          onClick: handlers.onClose,
          style: { border: "none", background: "transparent", fontSize: "20px", color: C.textMuted, cursor: "pointer" }
        }, "×"),
      ),

      // Toolbar — AI + manual add
      React.createElement("div", {
        style: { display: "flex", gap: "8px", marginBottom: "12px", flexWrap: "wrap" }
      },
        React.createElement("button", {
          onClick: handlers.onRunAi,
          disabled: handlers.aiRunning,
          style: {
            padding: "6px 14px", borderRadius: "6px", border: `1px solid ${C.gold}`,
            background: handlers.aiRunning ? C.warmGray : "#fff",
            color: handlers.aiRunning ? C.textMuted : C.gold,
            fontSize: "12px", fontWeight: 600,
            cursor: handlers.aiRunning ? "wait" : "pointer",
          }
        }, handlers.aiRunning ? "Analyzing…" : (suggestions.length ? "Run AI again" : "Get AI suggestions")),

        React.createElement("button", {
          onClick: handlers.onAddManual,
          style: {
            padding: "6px 14px", borderRadius: "6px", border: `1px solid ${C.navy}`,
            background: "#fff", color: C.navy, fontSize: "12px",
            fontWeight: 600, cursor: "pointer",
          }
        }, "+ Add manual substitution"),

        textPreview !== null && React.createElement("button", {
          onClick: () => setPreviewOpen(v => !v),
          style: {
            padding: "6px 14px", borderRadius: "6px", border: `1px solid ${C.warmGray}`,
            background: "#fff", color: C.textDark, fontSize: "12px",
            fontWeight: 500, cursor: "pointer",
          }
        }, previewOpen ? "Hide document text" : "Show document text"),
      ),

      // Document text preview (collapsible reference for manual entries)
      previewOpen && React.createElement("div", {
        style: {
          background: C.warmWhite, padding: "10px 12px", borderRadius: "6px",
          marginBottom: "12px", maxHeight: "180px", overflowY: "auto",
          fontSize: "11px", fontFamily: "monospace", color: C.textDark,
          whiteSpace: "pre-wrap", border: `1px solid ${C.warmGray}`,
        }
      }, textLoading ? "Loading document text…" : (textPreview || "(no text extracted)")),

      // Body: scrollable substitutions list
      React.createElement("div", {
        style: { flex: 1, overflowY: "auto", paddingRight: "4px", minHeight: "120px" }
      },
        suggestions.length === 0
          ? React.createElement("div", {
              style: { padding: "40px", textAlign: "center", color: C.textMuted, fontSize: "13px" }
            }, "No substitutions yet. Click ",
              React.createElement("strong", null, "Get AI suggestions"),
              " to have Claude scan the document, or ",
              React.createElement("strong", null, "+ Add manual substitution"),
              " to type one yourself."
            )
          : React.createElement("div", null,
              React.createElement("p", {
                style: { fontSize: "12px", color: C.textMuted, marginBottom: "12px", background: C.warmWhite, padding: "8px 12px", borderRadius: "6px" }
              },
                "Type the exact text as it appears in the document (left) and the merge-field name (right). Uncheck anything you don't want applied. Remove a row with the × button."),

              ...suggestions.map((s, idx) => {
                const isChecked = checked.has(idx);
                const isNew = s.is_new_field;
                const lowConf = (s.confidence ?? 1) < 0.5;
                const isManual = s.notes === "manual";
                return React.createElement("div", {
                  key: idx,
                  style: {
                    display: "flex", gap: "10px", padding: "10px 12px",
                    borderBottom: `1px solid ${C.warmGray}`,
                    background: isChecked ? C.warmWhite : "transparent",
                  }
                },
                  React.createElement("input", {
                    type: "checkbox", checked: isChecked,
                    onChange: () => handlers.onToggle(idx),
                    style: { marginTop: "3px", cursor: "pointer" },
                  }),
                  React.createElement("div", { style: { flex: 1, minWidth: 0 } },
                    React.createElement("div", {
                      style: { display: "flex", gap: "8px", alignItems: "center", marginBottom: "4px", flexWrap: "wrap" }
                    },
                      // Original text — editable when manual OR always
                      // (users can fix AI-detected snippets too)
                      React.createElement("input", {
                        type: "text",
                        value: s.original_text,
                        onChange: (e) => handlers.onEditOriginal(idx, e.target.value),
                        placeholder: isManual ? "Text in the document (exact)" : "",
                        title: s.original_text,
                        style: {
                          padding: "2px 6px",
                          border: `1px solid ${isManual && !s.original_text ? C.error : C.warmGray}`,
                          borderRadius: "4px", fontSize: "12px", fontFamily: "monospace",
                          background: "#fff", minWidth: "260px", flex: 1,
                        },
                      }),
                      React.createElement("span", { style: { fontSize: "11px", color: C.textMuted } }, "→"),
                      React.createElement("input", {
                        type: "text",
                        value: s.suggested_field,
                        onChange: (e) => handlers.onRename(idx, e.target.value),
                        placeholder: isManual ? "field_name" : "",
                        style: {
                          padding: "2px 6px",
                          border: `1px solid ${isManual && !s.suggested_field ? C.error : C.warmGray}`,
                          borderRadius: "4px", fontSize: "12px", fontFamily: "monospace",
                          background: "#fff", minWidth: "150px",
                        },
                      }),
                      isManual && React.createElement("span", {
                        style: {
                          fontSize: "10px", padding: "2px 6px", borderRadius: "3px",
                          background: "#EFF6FF", color: "#1D4ED8", fontWeight: 600,
                        }
                      }, "MANUAL"),
                      !isManual && isNew && React.createElement("span", {
                        style: {
                          fontSize: "10px", padding: "2px 6px", borderRadius: "3px",
                          background: "#FEF3C7", color: "#92400E", fontWeight: 600,
                        }
                      }, "NEW"),
                      lowConf && !isManual && React.createElement("span", {
                        style: {
                          fontSize: "10px", padding: "2px 6px", borderRadius: "3px",
                          background: "#FEE2E2", color: "#991B1B", fontWeight: 600,
                        },
                        title: `Confidence: ${(s.confidence * 100).toFixed(0)}%`
                      }, "LOW CONFIDENCE"),
                      React.createElement("button", {
                        onClick: () => handlers.onRemove(idx),
                        title: "Remove this substitution",
                        style: {
                          marginLeft: "auto", border: "none", background: "transparent",
                          color: C.textMuted, cursor: "pointer", fontSize: "14px",
                          padding: "0 4px",
                        }
                      }, "×"),
                    ),
                    s.context && !isManual && React.createElement("div", {
                      style: { fontSize: "11px", color: C.textMuted, fontStyle: "italic" }
                    }, "…" + s.context + "…"),
                    s.notes && !isManual && React.createElement("div", {
                      style: { fontSize: "11px", color: C.textMuted, marginTop: "2px" }
                    }, s.notes),
                  ),
                );
              }),
            ),
      ),

      // Footer
      React.createElement("div", {
        style: {
          display: "flex", justifyContent: "space-between", alignItems: "center",
          marginTop: "16px", paddingTop: "16px", borderTop: `1px solid ${C.warmGray}`,
        }
      },
        React.createElement("div", { style: { fontSize: "12px", color: C.textMuted } },
          `${selectedCount} of ${suggestions.length} selected`),
        React.createElement("div", { style: { display: "flex", gap: "8px" } },
          React.createElement("button", {
            onClick: handlers.onClose,
            disabled: handlers.applying,
            style: {
              padding: "8px 16px", borderRadius: "6px", border: `1px solid ${C.warmGray}`,
              background: "#fff", color: C.textMuted, fontSize: "13px",
              cursor: handlers.applying ? "default" : "pointer",
            }
          }, "Cancel"),
          React.createElement("button", {
            onClick: handlers.onApply,
            disabled: handlers.applying || selectedCount === 0,
            style: {
              padding: "8px 20px", borderRadius: "6px", border: "none",
              background: (handlers.applying || selectedCount === 0) ? C.warmGray : C.gold,
              color: "#fff", fontSize: "13px", fontWeight: 600,
              cursor: (handlers.applying || selectedCount === 0) ? "default" : "pointer",
            }
          }, handlers.applying ? "Applying…" : `Apply ${selectedCount} substitution${selectedCount === 1 ? "" : "s"}`),
        ),
      ),
    ),
  );
}

// ─── Logo upload widget (Substep 5-ε) ──────────────────────

function LogoUploadField({ canEdit, showNotification }) {
  // Cache-buster suffix so an <img> tag re-fetches after upload
  // (browser would otherwise keep serving the old logo from cache
  // even though we set Cache-Control: no-store server-side).
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [hasLogo, setHasLogo] = React.useState(null);   // null=loading, true/false=known
  const [uploading, setUploading] = React.useState(false);
  const [dragOver, setDragOver] = React.useState(false);
  const [widthInches, setWidthInches] = React.useState("");

  const probeLogo = React.useCallback(async () => {
    // HEAD the endpoint; 200 -> logo exists, 404 -> none
    try {
      const res = await fetch("/api/current-firm/logo", { credentials: "same-origin" });
      setHasLogo(res.ok);
    } catch {
      setHasLogo(false);
    }
  }, []);

  React.useEffect(() => { probeLogo(); }, [probeLogo]);

  const uploadFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      showNotification?.("Please pick an image (PNG, JPEG, SVG, GIF, or WebP).", "warning");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showNotification?.("Logo too large (max 5 MB).", "warning");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (widthInches && parseFloat(widthInches) > 0) {
        fd.append("width_inches", widthInches);
      }
      const res = await fetch("/api/current-firm/logo", {
        method: "PUT", body: fd, credentials: "same-origin",
      });
      if (!res.ok) {
        let msg = res.statusText;
        try { const j = await res.json(); msg = j.detail || msg; } catch {}
        throw new Error(msg);
      }
      showNotification?.("Logo uploaded.", "success");
      setHasLogo(true);
      setRefreshKey(k => k + 1);
    } catch (e) {
      showNotification?.(`Upload failed: ${e.message}`, "error");
    }
    setUploading(false);
  };

  const removeLogo = async () => {
    if (!window.confirm("Remove the firm logo? Any template referencing {{firm_logo}} will render the placeholder text again until a new one is uploaded.")) return;
    try {
      const res = await fetch("/api/current-firm/logo", {
        method: "DELETE", credentials: "same-origin",
      });
      if (!res.ok) {
        let msg = res.statusText;
        try { const j = await res.json(); msg = j.detail || msg; } catch {}
        throw new Error(msg);
      }
      showNotification?.("Logo removed.", "success");
      setHasLogo(false);
      setRefreshKey(k => k + 1);
    } catch (e) {
      showNotification?.(`Remove failed: ${e.message}`, "error");
    }
  };

  return React.createElement("div", { style: { marginBottom: "16px" } },
    React.createElement("label", {
      style: { display: "block", fontSize: "13px", fontWeight: 600, color: C.navy, marginBottom: "4px" }
    }, "Firm logo"),
    React.createElement("div", {
      style: { fontSize: "12px", color: C.textMuted, marginBottom: "8px" }
    }, "Uploaded once. Auto-inserted at any ",
      React.createElement("code", {
        style: { background: C.warmWhite, padding: "1px 4px", borderRadius: "3px", fontFamily: "monospace" }
      }, "{{firm_logo}}"),
      " placeholder in your document templates. Max 5 MB. PNG / JPEG / SVG."
    ),

    // Preview + actions row
    hasLogo === null
      ? React.createElement("div", { style: { color: C.textMuted, fontSize: "13px" } }, "Checking…")
      : hasLogo
        ? React.createElement("div", {
            style: {
              display: "flex", alignItems: "center", gap: "16px",
              padding: "12px", background: C.warmWhite,
              borderRadius: "6px", border: `1px solid ${C.warmGray}`,
            }
          },
            React.createElement("img", {
              src: `/api/current-firm/logo?v=${refreshKey}`,
              alt: "Firm logo",
              style: {
                maxHeight: "80px", maxWidth: "200px",
                background: "#fff", padding: "4px", borderRadius: "4px",
                objectFit: "contain",
              },
            }),
            canEdit && React.createElement("div", { style: { display: "flex", gap: "6px" } },
              React.createElement("label", {
                style: {
                  padding: "6px 12px", border: `1px solid ${C.warmGray}`,
                  background: "#fff", color: C.textDark, borderRadius: "6px",
                  fontSize: "12px", cursor: uploading ? "wait" : "pointer",
                }
              },
                uploading ? "Uploading…" : "Replace",
                React.createElement("input", {
                  type: "file", accept: "image/*", style: { display: "none" },
                  disabled: uploading,
                  onChange: (e) => { uploadFile(e.target.files?.[0]); e.target.value = ""; },
                }),
              ),
              React.createElement("button", {
                onClick: removeLogo,
                style: {
                  padding: "6px 12px", border: `1px solid ${C.warmGray}`,
                  background: "#fff", color: C.error, borderRadius: "6px",
                  fontSize: "12px", cursor: "pointer",
                }
              }, "Remove"),
            ),
          )
        : canEdit && React.createElement("label", {
            onDragOver: (e) => { e.preventDefault(); setDragOver(true); },
            onDragLeave: () => setDragOver(false),
            onDrop: (e) => {
              e.preventDefault();
              setDragOver(false);
              const f = e.dataTransfer?.files?.[0];
              if (f) uploadFile(f);
            },
            style: {
              display: "block", padding: "20px", textAlign: "center",
              border: `2px dashed ${dragOver ? C.gold : C.warmGray}`,
              borderRadius: "8px", cursor: "pointer",
              background: dragOver ? "#FFF6E5" : C.warmWhite,
              color: C.textMuted, fontSize: "13px",
              transition: "background 0.15s, border-color 0.15s",
            },
          },
            React.createElement("div", { style: { fontWeight: 600, color: C.navy, marginBottom: "2px" } },
              "Drop a logo here or click to pick"),
            React.createElement("div", { style: { fontSize: "11px" } },
              "PNG / JPEG / SVG · up to 5 MB"),
            React.createElement("input", {
              type: "file", accept: "image/*", style: { display: "none" },
              disabled: uploading,
              onChange: (e) => { uploadFile(e.target.files?.[0]); e.target.value = ""; },
            }),
          ),

    // Width control (only useful once a logo is uploaded)
    canEdit && hasLogo && React.createElement("div", { style: { marginTop: "10px" } },
      React.createElement("label", {
        style: { fontSize: "11px", color: C.textMuted, display: "block", marginBottom: "3px" }
      }, "Insertion width (inches, blank = 2.0 default)"),
      React.createElement("input", {
        type: "number", step: "0.1", min: "0.5", max: "8",
        value: widthInches, onChange: (e) => setWidthInches(e.target.value),
        placeholder: "2.0",
        style: {
          width: "100px", padding: "6px 8px", border: `1px solid ${C.warmGray}`,
          borderRadius: "6px", fontSize: "12px",
        }
      }),
      React.createElement("span", { style: { fontSize: "11px", color: C.textMuted, marginLeft: "8px" } },
        "Applies on next upload — re-upload to change."),
    ),
  );
}


function _fmtBytes(n) {
  if (n < 1024) return n + " B";
  if (n < 1024 * 1024) return (n / 1024).toFixed(1) + " KB";
  return (n / (1024 * 1024)).toFixed(1) + " MB";
}


// ─── Firm Vault — encrypted external-service creds (Substep 5-θ.2) ──
// List / create / edit / delete + audit-logged reveal. Backed by
// /api/firm-credentials/* — password Fernet-encrypted at rest.
// Reveal shows plaintext + copies to clipboard, auto-hides after 30s.

const _VAULT_EMPTY_FORM = {
  service_key: "", service_label: "", username: "",
  password: "", url: "", notes: "",
};
const _REVEAL_TTL_MS = 30_000;

function VaultSection({ canEdit, showNotification }) {
  const [creds, setCreds] = React.useState(null);        // null = loading
  const [loadError, setLoadError] = React.useState(null);
  const [editor, setEditor] = React.useState(null);      // null | {mode:'new'|'edit', form, service_key?}
  const [saving, setSaving] = React.useState(false);
  const [pendingKey, setPendingKey] = React.useState(null); // service_key being deleted/revealed
  // revealed = { [service_key]: { field, value, hideAt } }
  const [revealed, setRevealed] = React.useState({});
  const timersRef = React.useRef({});

  const load = React.useCallback(async () => {
    try {
      const d = await apiFetch("/api/firm-credentials");
      setCreds(d.data || []);
      setLoadError(null);
    } catch (e) {
      setLoadError(e.message || String(e));
      setCreds([]);
    }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  // Cleanup pending auto-hide timers on unmount.
  React.useEffect(() => () => {
    Object.values(timersRef.current).forEach(t => clearTimeout(t));
  }, []);

  const openNew = () => setEditor({ mode: "new", form: { ..._VAULT_EMPTY_FORM } });

  const openEdit = (cred) => setEditor({
    mode: "edit",
    service_key: cred.service_key,
    form: {
      service_key: cred.service_key,
      service_label: cred.service_label || "",
      username: cred.username || "",
      password: "",                // empty by default; empty on save = leave alone
      url: cred.url || "",
      notes: cred.notes || "",
    },
  });

  const save = async () => {
    if (!editor) return;
    const { mode, form, service_key: origKey } = editor;
    if (!form.service_key.trim() || !form.service_label.trim()) {
      showNotification?.("service_key and service_label are required.", "warning");
      return;
    }
    setSaving(true);
    try {
      const body = {
        service_key: form.service_key.trim().toLowerCase(),
        service_label: form.service_label.trim(),
        username: form.username.trim() || null,
        url: form.url.trim() || null,
        notes: form.notes.trim() || null,
      };
      // Password semantics: empty on edit = leave alone; empty on new = no password stored.
      if (mode === "new") {
        if (form.password) body.password = form.password;
        await apiFetch("/api/firm-credentials", {
          method: "POST",
          body: JSON.stringify(body),
        });
        showNotification?.(`Credential "${body.service_label}" saved.`, "success");
      } else {
        if (form.password) body.password = form.password;
        await apiFetch(`/api/firm-credentials/${origKey}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        });
        showNotification?.("Credential updated.", "success");
      }
      setEditor(null);
      await load();
    } catch (e) {
      // 503 = vault disabled (missing CHARPEN_CREDENTIAL_KEY).
      const msg = e.status === 503
        ? "Vault is disabled — CHARPEN_CREDENTIAL_KEY not set in Railway env."
        : `Save failed: ${e.message}`;
      showNotification?.(msg, "error");
    }
    setSaving(false);
  };

  const remove = async (cred) => {
    if (!window.confirm(`Delete credential "${cred.service_label}" (${cred.service_key})?\n\nThis is not reversible.`)) return;
    setPendingKey(cred.service_key);
    try {
      await apiFetch(`/api/firm-credentials/${cred.service_key}`, { method: "DELETE" });
      showNotification?.("Credential deleted.", "success");
      await load();
    } catch (e) {
      showNotification?.(`Delete failed: ${e.message}`, "error");
    }
    setPendingKey(null);
  };

  const reveal = async (cred, field) => {
    setPendingKey(cred.service_key + ":" + field);
    try {
      const resp = await apiFetch(`/api/firm-credentials/${cred.service_key}/reveal`, {
        method: "POST",
        body: JSON.stringify({ field }),
      });
      const value = resp.value ?? "";
      const hideAt = Date.now() + _REVEAL_TTL_MS;
      setRevealed(prev => ({ ...prev, [cred.service_key]: { field, value, hideAt } }));
      // Copy to clipboard when supported. Graceful fallback: user can still see it.
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(value);
          showNotification?.(`${field} copied to clipboard.`, "success");
        }
      } catch {}
      // Auto-hide.
      const existing = timersRef.current[cred.service_key];
      if (existing) clearTimeout(existing);
      timersRef.current[cred.service_key] = setTimeout(() => {
        setRevealed(prev => {
          const next = { ...prev };
          delete next[cred.service_key];
          return next;
        });
        delete timersRef.current[cred.service_key];
      }, _REVEAL_TTL_MS);
    } catch (e) {
      const msg = e.status === 503
        ? "Vault is disabled — CHARPEN_CREDENTIAL_KEY not set in Railway env."
        : `Reveal failed: ${e.message}`;
      showNotification?.(msg, "error");
    }
    setPendingKey(null);
  };

  const hideReveal = (service_key) => {
    setRevealed(prev => {
      const next = { ...prev };
      delete next[service_key];
      return next;
    });
    if (timersRef.current[service_key]) {
      clearTimeout(timersRef.current[service_key]);
      delete timersRef.current[service_key];
    }
  };

  return React.createElement("div", {
    style: {
      background: "#fff", borderRadius: "12px", padding: "20px 24px",
      border: `1px solid ${C.warmGray}`, marginBottom: "20px",
    }
  },
    // Header
    React.createElement("div", {
      style: {
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginBottom: "12px", gap: "8px", flexWrap: "wrap",
      }
    },
      React.createElement("div", null,
        React.createElement("h2", {
          style: {
            fontSize: "13px", color: C.textMuted, textTransform: "uppercase",
            letterSpacing: "0.08em", fontWeight: 600, margin: 0,
          }
        }, `Firm Vault${creds ? ` (${creds.length})` : ""}`),
        React.createElement("div", {
          style: { fontSize: "12px", color: C.textMuted, marginTop: "4px" }
        }, `Encrypted credentials for external services. Reference in SOPs via `,
          React.createElement("code", {
            style: { background: C.warmWhite, padding: "1px 4px", borderRadius: "3px", fontSize: "11px" }
          }, "{{creds.<key>.<field>}}"),
          "."),
      ),
      canEdit && React.createElement("button", {
        onClick: openNew,
        disabled: saving,
        style: {
          padding: "6px 14px", borderRadius: "8px", border: "none",
          background: C.gold, color: "#fff",
          fontSize: "12px", fontWeight: 600, cursor: "pointer",
        },
      }, "+ Add credential"),
    ),

    // Load state / empty / list
    creds === null
      ? React.createElement("div", {
          style: { padding: "16px", fontSize: "12px", color: C.textMuted }
        }, "Loading…")
      : loadError
        ? React.createElement("div", {
            style: {
              padding: "12px 14px", background: C.errorBg, color: C.error,
              borderRadius: "8px", fontSize: "12px",
            }
          }, `Failed to load credentials: ${loadError}`)
        : creds.length === 0
          ? React.createElement("div", {
              style: {
                padding: "20px", textAlign: "center", fontSize: "13px",
                color: C.textMuted, background: C.warmWhite, borderRadius: "8px",
              }
            }, "No credentials yet. ",
              canEdit && React.createElement("strong", null, "+ Add credential"),
              " to get started.")
          : React.createElement("div", {
              style: {
                display: "flex", flexDirection: "column",
                border: `1px solid ${C.warmGray}`, borderRadius: "8px",
                overflow: "hidden",
              }
            },
              ...creds.map((cred, i) => _renderVaultRow(cred, {
                first: i === 0,
                canEdit,
                pending: pendingKey === cred.service_key,
                revealed: revealed[cred.service_key],
                onReveal: (field) => reveal(cred, field),
                onHide: () => hideReveal(cred.service_key),
                onEdit: () => openEdit(cred),
                onDelete: () => remove(cred),
                revealPending: (field) => pendingKey === cred.service_key + ":" + field,
              })),
            ),

    // Editor modal
    editor && _renderVaultEditor(editor, {
      onChange: (updates) => setEditor({ ...editor, form: { ...editor.form, ...updates } }),
      onClose: () => !saving && setEditor(null),
      onSave: save,
      saving,
    }),
  );
}

function _renderVaultRow(cred, opts) {
  const { first, canEdit, pending, revealed, onReveal, onHide, onEdit, onDelete, revealPending } = opts;
  const passwordShown = revealed && revealed.field === "password";
  return React.createElement("div", {
    key: cred.service_key,
    style: {
      padding: "12px 16px", background: "#fff",
      borderTop: first ? "none" : `1px solid ${C.warmGray}`,
      display: "flex", flexDirection: "column", gap: "6px",
    }
  },
    // Row 1: label + key + edit/delete
    React.createElement("div", {
      style: { display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }
    },
      React.createElement("span", { style: { fontSize: "13px", fontWeight: 600, color: C.navy } },
        cred.service_label),
      React.createElement("code", {
        style: {
          fontSize: "11px", padding: "2px 6px", borderRadius: "4px",
          background: C.warmWhite, color: C.textMuted, fontFamily: "monospace",
        },
        title: `Placeholder token: {{creds.${cred.service_key}.<field>}}`,
      }, cred.service_key),
      canEdit && React.createElement("div", {
        style: { marginLeft: "auto", display: "flex", gap: "4px" }
      },
        React.createElement("button", {
          onClick: onEdit, disabled: pending,
          style: {
            padding: "3px 10px", borderRadius: "6px",
            border: `1px solid ${C.warmGray}`, background: "#fff",
            color: C.textDark, fontSize: "11px", cursor: "pointer",
          }
        }, "Edit"),
        React.createElement("button", {
          onClick: onDelete, disabled: pending,
          style: {
            padding: "3px 10px", borderRadius: "6px",
            border: `1px solid ${C.warmGray}`, background: "#fff",
            color: C.error, fontSize: "11px", cursor: "pointer",
          }
        }, "Delete"),
      ),
    ),
    // Row 2: username with reveal
    cred.username && React.createElement("div", {
      style: { display: "flex", alignItems: "center", gap: "8px", fontSize: "12px" }
    },
      React.createElement("span", { style: { color: C.textMuted, minWidth: "70px" } }, "Username"),
      React.createElement("code", {
        style: {
          background: C.warmWhite, padding: "2px 8px", borderRadius: "4px",
          fontFamily: "monospace", color: C.textDark, flex: 1,
        }
      }, cred.username),
      React.createElement("button", {
        onClick: () => {
          navigator.clipboard?.writeText(cred.username);
          onReveal("username");
        },
        disabled: revealPending("username"),
        style: {
          padding: "2px 8px", borderRadius: "4px",
          border: `1px solid ${C.warmGray}`, background: "#fff",
          color: C.textMuted, fontSize: "11px", cursor: "pointer",
        },
        title: "Copy + log a reveal event",
      }, "Copy"),
    ),
    // Row 3: password (reveal or masked)
    React.createElement("div", {
      style: { display: "flex", alignItems: "center", gap: "8px", fontSize: "12px" }
    },
      React.createElement("span", { style: { color: C.textMuted, minWidth: "70px" } }, "Password"),
      cred.has_password
        ? (passwordShown
            ? React.createElement(React.Fragment, null,
                React.createElement("code", {
                  style: {
                    background: `${C.gold}18`, padding: "2px 8px", borderRadius: "4px",
                    fontFamily: "monospace", color: C.textDark, flex: 1,
                    userSelect: "all",
                  }
                }, revealed.value),
                React.createElement("button", {
                  onClick: onHide,
                  style: {
                    padding: "2px 8px", borderRadius: "4px",
                    border: `1px solid ${C.warmGray}`, background: "#fff",
                    color: C.textMuted, fontSize: "11px", cursor: "pointer",
                  },
                  title: "Hide (also auto-hides after 30s)",
                }, "Hide"),
              )
            : React.createElement(React.Fragment, null,
                React.createElement("code", {
                  style: {
                    background: C.warmWhite, padding: "2px 8px", borderRadius: "4px",
                    fontFamily: "monospace", color: C.textMuted, flex: 1,
                    letterSpacing: "0.2em",
                  }
                }, "••••••••••"),
                React.createElement("button", {
                  onClick: () => onReveal("password"),
                  disabled: revealPending("password"),
                  style: {
                    padding: "2px 10px", borderRadius: "4px",
                    border: `1px solid ${C.gold}`, background: `${C.gold}18`,
                    color: C.gold, fontSize: "11px", cursor: "pointer",
                    fontWeight: 600,
                  },
                  title: "Reveal + copy to clipboard (audit-logged)",
                }, revealPending("password") ? "…" : "Reveal"),
              )
          )
        : React.createElement("span", {
            style: { color: C.textLight, fontStyle: "italic", flex: 1 }
          }, "(not set)"),
    ),
    // Row 4: URL
    cred.url && React.createElement("div", {
      style: { display: "flex", alignItems: "center", gap: "8px", fontSize: "12px" }
    },
      React.createElement("span", { style: { color: C.textMuted, minWidth: "70px" } }, "URL"),
      React.createElement("a", {
        href: cred.url, target: "_blank", rel: "noopener noreferrer",
        style: {
          color: C.navy, textDecoration: "underline", flex: 1,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }
      }, cred.url),
    ),
    // Row 5: notes
    cred.notes && React.createElement("div", {
      style: { display: "flex", alignItems: "flex-start", gap: "8px", fontSize: "12px" }
    },
      React.createElement("span", { style: { color: C.textMuted, minWidth: "70px" } }, "Notes"),
      React.createElement("span", { style: { color: C.textDark, flex: 1, whiteSpace: "pre-wrap" } },
        cred.notes),
    ),
  );
}

function _renderVaultEditor(editor, handlers) {
  const { mode, form } = editor;
  const set = (k) => (e) => handlers.onChange({ [k]: e.target.value });
  return React.createElement("div", {
    onClick: (e) => { if (e.target === e.currentTarget) handlers.onClose(); },
    style: {
      position: "fixed", inset: 0, background: "rgba(27, 42, 74, 0.6)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000, padding: "40px 20px",
    }
  },
    React.createElement("div", {
      style: {
        background: "#fff", borderRadius: "12px", padding: "24px",
        maxWidth: "560px", width: "100%", maxHeight: "90vh",
        display: "flex", flexDirection: "column", gap: "12px",
      }
    },
      React.createElement("h3", {
        style: { fontFamily: "'Playfair Display',serif", fontSize: "20px", color: C.navy, margin: 0 }
      }, mode === "new" ? "Add credential" : "Edit credential"),

      _field("Service label *",
        "Human-friendly name shown in the vault (e.g. 'PACER Login').",
        _textInput(form.service_label, set("service_label"), true)),

      _field("Service key *",
        "Lowercase snake_case identifier used in SOP placeholders — e.g. 'pacer' → {{creds.pacer.password}}. Can't be changed after create.",
        React.createElement("input", {
          type: "text",
          value: form.service_key,
          onChange: set("service_key"),
          disabled: mode === "edit",
          placeholder: "pacer",
          style: {
            width: "100%", padding: "8px 10px", border: `1px solid ${C.warmGray}`,
            borderRadius: "6px", fontSize: "13px", boxSizing: "border-box",
            fontFamily: "monospace",
            background: mode === "edit" ? C.warmWhite : "#fff",
            color: mode === "edit" ? C.textMuted : C.textDark,
          }
        })),

      _field("Username", null, _textInput(form.username, set("username"), true)),

      _field("Password",
        mode === "edit"
          ? "Leave blank to keep the existing password. Enter a new value to replace it."
          : "Stored encrypted at rest.",
        React.createElement("input", {
          type: "password",
          value: form.password,
          onChange: set("password"),
          placeholder: mode === "edit" ? "(unchanged)" : "",
          style: {
            width: "100%", padding: "8px 10px", border: `1px solid ${C.warmGray}`,
            borderRadius: "6px", fontSize: "13px", boxSizing: "border-box",
          }
        })),

      _field("URL", "Login URL — optional.",
        _textInput(form.url, set("url"), true)),

      _field("Notes", "Internal notes (e.g. 'MFA code goes to Aaron's phone'). Plaintext, not encrypted.",
        _textArea(form.notes, set("notes"), true, 3)),

      React.createElement("div", {
        style: {
          display: "flex", justifyContent: "flex-end", gap: "8px",
          marginTop: "8px", paddingTop: "12px",
          borderTop: `1px solid ${C.warmGray}`,
        }
      },
        React.createElement("button", {
          onClick: handlers.onClose, disabled: handlers.saving,
          style: {
            padding: "8px 16px", borderRadius: "6px",
            border: `1px solid ${C.warmGray}`, background: "#fff",
            color: C.textMuted, fontSize: "13px",
            cursor: handlers.saving ? "default" : "pointer",
          }
        }, "Cancel"),
        React.createElement("button", {
          onClick: handlers.onSave,
          disabled: handlers.saving || !form.service_key.trim() || !form.service_label.trim(),
          style: {
            padding: "8px 20px", borderRadius: "6px", border: "none",
            background: (handlers.saving || !form.service_key.trim() || !form.service_label.trim())
              ? C.warmGray : C.gold,
            color: "#fff", fontSize: "13px", fontWeight: 600,
            cursor: (handlers.saving || !form.service_key.trim() || !form.service_label.trim())
              ? "default" : "pointer",
          }
        }, handlers.saving ? "Saving…" : (mode === "new" ? "Create" : "Save changes")),
      ),
    ),
  );
}


// ─── Firm Locations — multi-office CRUD (Substep 5-κ.2) ─────
// Attorneys pick their location via My Profile (5-κ.3). Doc
// generation resolves letterhead from responsible attorney's
// location (5-κ.4). List / add / edit / delete + set-default.

const _LOC_EMPTY_FORM = {
  name: "", slug: "", address: "", phone: "", fax: "",
  email: "", bar_states: "", is_default: false,
};

function LocationsSection({ canEdit, showNotification }) {
  const [locations, setLocations] = React.useState(null);
  const [loadError, setLoadError] = React.useState(null);
  const [editor, setEditor] = React.useState(null);   // {mode, form, id?}
  const [saving, setSaving] = React.useState(false);
  const [pendingId, setPendingId] = React.useState(null);

  const load = React.useCallback(async () => {
    try {
      const d = await apiFetch("/api/firm-locations");
      setLocations(d.data || []);
      setLoadError(null);
    } catch (e) {
      setLoadError(e.message || String(e));
      setLocations([]);
    }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const openNew = () => setEditor({ mode: "new", form: { ..._LOC_EMPTY_FORM } });
  const openEdit = (loc) => setEditor({
    mode: "edit",
    id: loc.id,
    form: {
      name: loc.name || "",
      slug: loc.slug || "",
      address: loc.address || "",
      phone: loc.phone || "",
      fax: loc.fax || "",
      email: loc.email || "",
      bar_states: loc.bar_states || "",
      is_default: !!loc.is_default,
    },
  });

  const save = async () => {
    if (!editor) return;
    const { mode, id, form } = editor;
    if (!form.name.trim()) {
      showNotification?.("name is required.", "warning");
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        address: form.address.trim() || null,
        phone: form.phone.trim() || null,
        fax: form.fax.trim() || null,
        email: form.email.trim() || null,
        bar_states: form.bar_states.trim() || null,
        is_default: !!form.is_default,
      };
      if (mode === "new") {
        // slug is server-derived from name unless caller supplied one
        if (form.slug.trim()) body.slug = form.slug.trim().toLowerCase();
        await apiFetch("/api/firm-locations", {
          method: "POST",
          body: JSON.stringify(body),
        });
        showNotification?.(`Location "${body.name}" saved.`, "success");
      } else {
        await apiFetch(`/api/firm-locations/${id}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        });
        showNotification?.("Location updated.", "success");
      }
      setEditor(null);
      await load();
    } catch (e) {
      showNotification?.(`Save failed: ${e.message}`, "error");
    }
    setSaving(false);
  };

  const remove = async (loc) => {
    if (!window.confirm(
      `Delete location "${loc.name}" (${loc.slug})?\n\nAttorneys currently assigned here will be reassigned to the firm's default location.`
    )) return;
    setPendingId(loc.id);
    try {
      await apiFetch(`/api/firm-locations/${loc.id}`, { method: "DELETE" });
      showNotification?.("Location deleted.", "success");
      await load();
    } catch (e) {
      showNotification?.(`Delete failed: ${e.message}`, "error");
    }
    setPendingId(null);
  };

  const promoteToDefault = async (loc) => {
    setPendingId(loc.id);
    try {
      await apiFetch(`/api/firm-locations/${loc.id}`, {
        method: "PATCH",
        body: JSON.stringify({ is_default: true }),
      });
      showNotification?.(`"${loc.name}" is now the default location.`, "success");
      await load();
    } catch (e) {
      showNotification?.(`Update failed: ${e.message}`, "error");
    }
    setPendingId(null);
  };

  return React.createElement("div", {
    style: {
      background: "#fff", borderRadius: "12px", padding: "20px 24px",
      border: `1px solid ${C.warmGray}`, marginBottom: "20px",
    }
  },
    // Header
    React.createElement("div", {
      style: {
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginBottom: "12px", gap: "8px", flexWrap: "wrap",
      }
    },
      React.createElement("div", null,
        React.createElement("h2", {
          style: {
            fontSize: "13px", color: C.textMuted, textTransform: "uppercase",
            letterSpacing: "0.08em", fontWeight: 600, margin: 0,
          }
        }, `Firm Locations${locations ? ` (${locations.length})` : ""}`),
        React.createElement("div", {
          style: { fontSize: "12px", color: C.textMuted, marginTop: "4px" }
        }, "Physical offices. Attorneys pick their location in My Profile. Document letterhead resolves from the responsible attorney's location."),
      ),
      canEdit && React.createElement("button", {
        onClick: openNew,
        disabled: saving,
        style: {
          padding: "6px 14px", borderRadius: "8px", border: "none",
          background: C.gold, color: "#fff",
          fontSize: "12px", fontWeight: 600, cursor: "pointer",
        },
      }, "+ Add location"),
    ),

    // Load state / list
    locations === null
      ? React.createElement("div", {
          style: { padding: "16px", fontSize: "12px", color: C.textMuted }
        }, "Loading…")
      : loadError
        ? React.createElement("div", {
            style: {
              padding: "12px 14px", background: C.errorBg, color: C.error,
              borderRadius: "8px", fontSize: "12px",
            }
          }, `Failed to load locations: ${loadError}`)
        : locations.length === 0
          ? React.createElement("div", {
              style: {
                padding: "20px", textAlign: "center", fontSize: "13px",
                color: C.textMuted, background: C.warmWhite, borderRadius: "8px",
              }
            }, "No locations yet.")
          : React.createElement("div", {
              style: {
                display: "flex", flexDirection: "column",
                border: `1px solid ${C.warmGray}`, borderRadius: "8px",
                overflow: "hidden",
              }
            },
              ...locations.map((loc, i) => _renderLocationRow(loc, {
                first: i === 0,
                canEdit,
                pending: pendingId === loc.id,
                onEdit: () => openEdit(loc),
                onDelete: () => remove(loc),
                onPromote: () => promoteToDefault(loc),
              })),
            ),

    // Editor modal
    editor && _renderLocationEditor(editor, {
      onChange: (updates) => setEditor({ ...editor, form: { ...editor.form, ...updates } }),
      onClose: () => !saving && setEditor(null),
      onSave: save,
      saving,
    }),
  );
}

function _renderLocationRow(loc, opts) {
  const { first, canEdit, pending, onEdit, onDelete, onPromote } = opts;
  return React.createElement("div", {
    key: loc.id,
    style: {
      padding: "12px 16px", background: "#fff",
      borderTop: first ? "none" : `1px solid ${C.warmGray}`,
      display: "flex", flexDirection: "column", gap: "6px",
    }
  },
    // Row 1: name + slug + default badge + edit/delete
    React.createElement("div", {
      style: { display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }
    },
      React.createElement("span", { style: { fontSize: "13px", fontWeight: 600, color: C.navy } },
        loc.name),
      React.createElement("code", {
        style: {
          fontSize: "11px", padding: "2px 6px", borderRadius: "4px",
          background: C.warmWhite, color: C.textMuted, fontFamily: "monospace",
        },
      }, loc.slug),
      loc.is_default && React.createElement("span", {
        style: {
          fontSize: "10px", padding: "2px 8px", borderRadius: "999px",
          background: `${C.gold}22`, color: C.textDark, fontWeight: 600,
          letterSpacing: "0.04em", textTransform: "uppercase",
        }
      }, "Default"),
      loc.bar_states && React.createElement("span", {
        style: {
          fontSize: "10px", padding: "2px 8px", borderRadius: "999px",
          background: `${C.navy}12`, color: C.navy, fontWeight: 600,
        },
        title: "Bar admission states",
      }, loc.bar_states),
      canEdit && React.createElement("div", {
        style: { marginLeft: "auto", display: "flex", gap: "4px" }
      },
        !loc.is_default && React.createElement("button", {
          onClick: onPromote, disabled: pending,
          style: {
            padding: "3px 10px", borderRadius: "6px",
            border: `1px solid ${C.warmGray}`, background: "#fff",
            color: C.textDark, fontSize: "11px", cursor: "pointer",
          },
          title: "Make this the firm's default location",
        }, "Set default"),
        React.createElement("button", {
          onClick: onEdit, disabled: pending,
          style: {
            padding: "3px 10px", borderRadius: "6px",
            border: `1px solid ${C.warmGray}`, background: "#fff",
            color: C.textDark, fontSize: "11px", cursor: "pointer",
          }
        }, "Edit"),
        !loc.is_default && React.createElement("button", {
          onClick: onDelete, disabled: pending,
          style: {
            padding: "3px 10px", borderRadius: "6px",
            border: `1px solid ${C.warmGray}`, background: "#fff",
            color: C.error, fontSize: "11px", cursor: "pointer",
          }
        }, "Delete"),
      ),
    ),
    // Address (multiline)
    loc.address && React.createElement("div", {
      style: { display: "flex", alignItems: "flex-start", gap: "8px", fontSize: "12px" }
    },
      React.createElement("span", { style: { color: C.textMuted, minWidth: "60px" } }, "Address"),
      React.createElement("span", { style: { color: C.textDark, flex: 1, whiteSpace: "pre-wrap" } },
        loc.address),
    ),
    // Phone/Fax/Email on a single line if compact
    (loc.phone || loc.fax || loc.email) && React.createElement("div", {
      style: { display: "flex", alignItems: "center", gap: "16px",
               fontSize: "12px", color: C.textMuted, flexWrap: "wrap" }
    },
      loc.phone && React.createElement("span", null,
        React.createElement("span", { style: { color: C.textLight } }, "Phone: "),
        loc.phone),
      loc.fax && React.createElement("span", null,
        React.createElement("span", { style: { color: C.textLight } }, "Fax: "),
        loc.fax),
      loc.email && React.createElement("span", null,
        React.createElement("span", { style: { color: C.textLight } }, "Email: "),
        loc.email),
    ),
  );
}

function _renderLocationEditor(editor, handlers) {
  const { mode, form } = editor;
  const set = (k) => (e) => handlers.onChange({ [k]: e.target.value });
  return React.createElement("div", {
    onClick: (e) => { if (e.target === e.currentTarget) handlers.onClose(); },
    style: {
      position: "fixed", inset: 0, background: "rgba(27, 42, 74, 0.6)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000, padding: "40px 20px",
    }
  },
    React.createElement("div", {
      style: {
        background: "#fff", borderRadius: "12px", padding: "24px",
        maxWidth: "620px", width: "100%", maxHeight: "90vh",
        display: "flex", flexDirection: "column", gap: "12px",
        overflowY: "auto",
      }
    },
      React.createElement("h3", {
        style: { fontFamily: "'Playfair Display',serif", fontSize: "20px", color: C.navy, margin: 0 }
      }, mode === "new" ? "Add location" : "Edit location"),

      _field("Name *",
        "Display name — e.g. 'Mobile Office' or 'Atlanta Office'.",
        _textInput(form.name, set("name"), true)),

      mode === "new" && _field("Slug",
        "URL-safe stable identifier. Leave blank to auto-derive from name (e.g. 'Atlanta Office' → 'atlanta_office'). Can't be changed after create.",
        React.createElement("input", {
          type: "text",
          value: form.slug,
          onChange: set("slug"),
          placeholder: "(auto)",
          style: {
            width: "100%", padding: "8px 10px", border: `1px solid ${C.warmGray}`,
            borderRadius: "6px", fontSize: "13px", boxSizing: "border-box",
            fontFamily: "monospace",
          }
        })),

      _field("Address",
        "Multi-line, free-form. Shown on letterheads.",
        _textArea(form.address, set("address"), true, 3)),

      React.createElement("div", { style: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" } },
        _field("Phone", null, _textInput(form.phone, set("phone"), true)),
        _field("Fax", null, _textInput(form.fax, set("fax"), true)),
      ),

      _field("Email", null, _textInput(form.email, set("email"), true)),

      _field("Bar states",
        "Semicolon-separated 2-letter state codes for attorneys sitting here — e.g. 'AL' or 'AL;GA'. Feeds state-specific compliance (SOL rules, etc.).",
        _textInput(form.bar_states, set("bar_states"), true)),

      React.createElement("label", {
        style: {
          display: "flex", alignItems: "center", gap: "8px",
          padding: "8px 0", fontSize: "13px", color: C.textDark, cursor: "pointer",
        }
      },
        React.createElement("input", {
          type: "checkbox",
          checked: !!form.is_default,
          onChange: (e) => handlers.onChange({ is_default: e.target.checked }),
        }),
        React.createElement("span", null,
          "Set as firm's default location ",
          React.createElement("span", { style: { color: C.textMuted, fontSize: "11px" } },
            "· used when an attorney has no location assigned"),
        ),
      ),

      React.createElement("div", {
        style: {
          display: "flex", justifyContent: "flex-end", gap: "8px",
          marginTop: "8px", paddingTop: "12px",
          borderTop: `1px solid ${C.warmGray}`,
        }
      },
        React.createElement("button", {
          onClick: handlers.onClose, disabled: handlers.saving,
          style: {
            padding: "8px 16px", borderRadius: "6px",
            border: `1px solid ${C.warmGray}`, background: "#fff",
            color: C.textMuted, fontSize: "13px",
            cursor: handlers.saving ? "default" : "pointer",
          }
        }, "Cancel"),
        React.createElement("button", {
          onClick: handlers.onSave,
          disabled: handlers.saving || !form.name.trim(),
          style: {
            padding: "8px 20px", borderRadius: "6px", border: "none",
            background: (handlers.saving || !form.name.trim())
              ? C.warmGray : C.gold,
            color: "#fff", fontSize: "13px", fontWeight: 600,
            cursor: (handlers.saving || !form.name.trim())
              ? "default" : "pointer",
          }
        }, handlers.saving ? "Saving…" : (mode === "new" ? "Create" : "Save changes")),
      ),
    ),
  );
}


// ─── Small helpers ───────────────────────────────────────────

// ─── Firm default fee agreement (PI Platform outline v2 §3.21) ──
//
// Its own component with its own save, deliberately separate from the
// page's brand/letterhead form. Those fields are cosmetic; this one
// decides how every settlement splits, and it should not ride along on
// a "Save changes" someone clicked to fix a phone number.
//
// This is the firm-wide DEFAULT. Matters inherit it and can override —
// a matter's own agreement always wins.
function FeeAgreementSection({ canEdit, showNotification }) {
  const [configured, setConfigured] = useState(null);
  const [basis, setBasis] = useState("gross");
  const [rate, setRate] = useState("");
  const [tiered, setTiered] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiFetch("/api/firm-fee-agreement")
      .then(r => {
        const d = r.data;
        setConfigured(!!d);
        if (d) {
          setBasis(d.fee_basis || "gross");
          setRate(d.flat_rate == null ? "" : String(d.flat_rate * 100));
          setTiered((d.tiers || []).length > 0);
          setTiers((d.tiers || []).map(t => ({ trigger: t.trigger, pct: String(t.rate * 100) })));
        }
      })
      .catch(() => setConfigured(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      // Percentages are entered the way people say them (33.33) and
      // stored as fractions. The API rejects a bare 33 — that is the
      // 100x error worth failing loudly on — so the conversion happens
      // here rather than by loosening the backend.
      await apiFetch("/api/firm-fee-agreement", {
        method: "PUT",
        body: JSON.stringify({
          fee_basis: basis,
          flat_rate: (tiered || rate === "") ? null : Number(rate) / 100,
          current_trigger: "pre_suit",
          tiers: tiered
            ? tiers.filter(t => t.pct !== "").map(t => ({ trigger: t.trigger, rate: Number(t.pct) / 100 }))
            : [],
        }),
      });
      setConfigured(true);
      showNotification?.("Default fee agreement saved.", "success");
    } catch (e) {
      showNotification?.(`Couldn't save: ${e.message}`, "error");
    }
    setSaving(false);
  };

  const setTier = (i, patch) => setTiers(ts => ts.map((t, j) => (j === i ? { ...t, ...patch } : t)));
  const sel = { padding: "7px 10px", borderRadius: "6px", border: `1px solid ${C.warmGray}`,
                fontSize: "13px", background: "#fff" };

  return _section("Default fee agreement",
    React.createElement("p", {
      style: { fontSize: "12px", color: C.textMuted, margin: "-8px 0 16px", lineHeight: 1.6 },
    }, "The firm's standard terms. New matters inherit these; a matter with its own "
     + "agreement overrides them. Used to work out what a client nets after fees, costs "
     + "and liens — with nothing configured that calculation refuses to run rather than "
     + "guess a rate."),

    _field("Fee is calculated on",
      "Whether the contingency comes off the gross settlement or after case costs are "
      + "deducted. On a $100,000 settlement with $10,000 of costs at one third, the two "
      + "conventions differ by $3,333 of the client's money — set this to match the signed "
      + "fee agreement.",
      React.createElement("select", {
        value: basis, disabled: !canEdit,
        onChange: e => setBasis(e.target.value),
        style: { ...sel, fontSize: "14px", padding: "8px 12px" },
      },
        React.createElement("option", { value: "gross" }, "Gross settlement"),
        React.createElement("option", { value: "net_of_costs" }, "Net of case costs"))),

    _field("Rate structure",
      "A flat percentage, or a sliding scale that steps up as the case progresses.",
      React.createElement("div", { style: { display: "flex", gap: "18px", alignItems: "center" } },
        React.createElement("label", { style: { display: "flex", gap: "6px", alignItems: "center", fontSize: "13px", cursor: "pointer" } },
          React.createElement("input", { type: "radio", checked: !tiered, disabled: !canEdit,
            onChange: () => setTiered(false) }), "Flat rate"),
        React.createElement("label", { style: { display: "flex", gap: "6px", alignItems: "center", fontSize: "13px", cursor: "pointer" } },
          React.createElement("input", { type: "radio", checked: tiered, disabled: !canEdit,
            onChange: () => {
              setTiered(true);
              if (tiers.length === 0) setTiers([
                { trigger: "pre_suit", pct: "33.33" }, { trigger: "suit_filed", pct: "40" }]);
            } }), "Sliding scale"))),

    !tiered && _field("Contingency percentage", "Entered as a percentage — 33.33 for one third.",
      React.createElement("div", { style: { display: "flex", alignItems: "center", gap: "8px" } },
        React.createElement("input", {
          type: "number", value: rate, disabled: !canEdit, step: "0.01", placeholder: "33.33",
          onChange: e => setRate(e.target.value),
          style: { ...sel, fontSize: "14px", padding: "8px 12px", width: "120px" },
        }),
        React.createElement("span", { style: { fontSize: "14px", color: C.textMuted } }, "%"))),

    tiered && _field("Sliding scale",
      "The percentage that applies at each stage. A matter uses the tier matching its "
      + "current stage; if none matches, the fee is not calculated rather than guessed "
      + "from a neighbouring tier.",
      React.createElement("div", { style: { display: "flex", flexDirection: "column", gap: "8px" } },
        ...tiers.map((t, i) => React.createElement("div", {
          key: i, style: { display: "flex", gap: "8px", alignItems: "center" },
        },
          React.createElement("select", {
            value: t.trigger, disabled: !canEdit, style: { ...sel, minWidth: "170px" },
            onChange: e => setTier(i, { trigger: e.target.value }),
          },
            React.createElement("option", { value: "pre_suit" }, "Settled pre-suit"),
            React.createElement("option", { value: "suit_filed" }, "Suit filed"),
            React.createElement("option", { value: "trial_set" }, "Trial set"),
            React.createElement("option", { value: "trial" }, "Tried"),
            React.createElement("option", { value: "appeal" }, "Appealed")),
          React.createElement("input", {
            type: "number", value: t.pct, disabled: !canEdit, step: "0.01",
            onChange: e => setTier(i, { pct: e.target.value }),
            style: { ...sel, width: "100px" },
          }),
          React.createElement("span", { style: { fontSize: "13px", color: C.textMuted } }, "%"),
          canEdit && React.createElement("button", {
            onClick: () => setTiers(ts => ts.filter((_, j) => j !== i)),
            style: { border: "none", background: "none", cursor: "pointer", color: C.textLight, fontSize: "16px", padding: "0 6px" },
          }, "\u00d7"))),
        canEdit && React.createElement("button", {
          onClick: () => setTiers(ts => [...ts, { trigger: "trial", pct: "" }]),
          style: { alignSelf: "flex-start", border: "none", background: "none", cursor: "pointer",
                   color: C.navy, fontSize: "12px", fontWeight: 600, padding: 0 },
        }, "Add a stage"))),

    canEdit && React.createElement("button", {
      onClick: save, disabled: saving,
      style: {
        padding: "9px 18px", borderRadius: "8px", border: "none",
        background: saving ? C.warmGray : C.gold, color: "#fff",
        fontSize: "13px", fontWeight: 600, cursor: saving ? "default" : "pointer",
      },
    }, saving ? "Saving\u2026" : "Save fee agreement"),

    // Names what is waiting on it. "Client-net calculations stay
    // unavailable" understated it — three screens are holding a blank
    // where a number belongs, and a reader has no way to know from
    // here that this is the thing standing in front of them.
    configured === false && React.createElement("div", {
      style: { fontSize: "12.5px", color: C.textDark, background: C.warmWhite,
               border: `1px solid ${C.warmGray}`, borderRadius: "8px",
               padding: "12px 15px", marginTop: "12px", lineHeight: 1.6 },
    },
      React.createElement("strong", null, "Not set yet. "),
      "Three things are waiting on this and cannot show a figure until "
      + "it is saved:",
      React.createElement("ul", { style: { margin: "6px 0 0 18px", padding: 0 } },
        React.createElement("li", null,
          "Finance → Revenue shows gross settlements only, not what "
          + "the firm made"),
        React.createElement("li", null,
          "The Revenue report is listed as unavailable, with this as the "
          + "reason"),
        React.createElement("li", null,
          "Settlement statements produce a draft with no fee line"),
      ),
    ),
  );
}


function _section(title, ...children) {
  return React.createElement("div", {
    style: {
      background: "#fff", borderRadius: "12px", padding: "20px 24px",
      border: `1px solid ${C.warmGray}`, marginBottom: "20px",
    }
  },
    React.createElement("h2", {
      style: {
        fontSize: "13px", color: C.textMuted, textTransform: "uppercase",
        letterSpacing: "0.08em", fontWeight: 600, margin: "0 0 16px 0",
      }
    }, title),
    ...children,
  );
}

function _field(label, hint, control) {
  return React.createElement("div", { style: { marginBottom: "16px" } },
    React.createElement("label", {
      style: { display: "block", fontSize: "13px", fontWeight: 600, color: C.navy, marginBottom: "4px" }
    }, label),
    hint && React.createElement("div", {
      style: { fontSize: "12px", color: C.textMuted, marginBottom: "6px" }
    }, hint),
    control,
  );
}

function _textInput(value, onChange, editable) {
  return React.createElement("input", {
    type: "text", value, onChange,
    disabled: !editable,
    style: {
      width: "100%", padding: "8px 10px", border: `1px solid ${C.warmGray}`,
      borderRadius: "6px", fontSize: "13px", boxSizing: "border-box",
      background: editable ? "#fff" : C.warmWhite,
      color: editable ? C.textDark : C.textMuted,
    }
  });
}

function _textArea(value, onChange, editable, rows = 3) {
  return React.createElement("textarea", {
    value, onChange, rows,
    disabled: !editable,
    style: {
      width: "100%", padding: "8px 10px", border: `1px solid ${C.warmGray}`,
      borderRadius: "6px", fontSize: "13px", boxSizing: "border-box",
      resize: "vertical", fontFamily: "inherit",
      background: editable ? "#fff" : C.warmWhite,
      color: editable ? C.textDark : C.textMuted,
    }
  });
}

function _colorInput(value, onChange, editable) {
  return React.createElement("div", { style: { display: "flex", gap: "8px", alignItems: "center" } },
    React.createElement("input", {
      type: "color", value: value || "#000000", onChange,
      disabled: !editable,
      style: { width: "48px", height: "36px", padding: 0, border: `1px solid ${C.warmGray}`, borderRadius: "6px", cursor: editable ? "pointer" : "default" }
    }),
    React.createElement("input", {
      type: "text", value: value || "", onChange,
      disabled: !editable,
      placeholder: "#000000",
      style: {
        padding: "8px 10px", border: `1px solid ${C.warmGray}`, borderRadius: "6px",
        fontSize: "13px", fontFamily: "monospace", width: "120px",
        background: editable ? "#fff" : C.warmWhite,
        color: editable ? C.textDark : C.textMuted,
      }
    }),
  );
}
